// ─── Collage Element: Slideshow Carousel ─────────────────────
// Draggable, resizable carousel on the freeform canvas.
// Transitions: slide (embla), crossfade (opacity timer), zoom (scale timer).

import React, { useEffect, useCallback, useState, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';

const SPEED_MAP = { slow: 5000, medium: 3000, fast: 2000 };
const API = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(url) {
  if (!url) return url;
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
}

export default function SlideshowElement({ element, isMobile, theme, skipPositioning }) {
  const style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);

  const images = (element.images || []).map(resolveUrl);
  const transition = element.transition || 'slide';
  const speed = SPEED_MAP[element.speed || 'medium'];
  const aspectRatio = element.aspectRatio || '16/9';
  const isTimerBased = transition === 'crossfade' || transition === 'zoom';

  // ── Embla (slide mode only) ──
  const plugins = [Autoplay({ delay: speed, stopOnInteraction: false, stopOnMouseEnter: true })];
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, duration: 25 },
    isTimerBased ? [] : plugins,
  );

  const [current, setCurrent] = useState(0);

  // ── Embla select sync (slide mode) ──
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrent(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (isTimerBased || !emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect, isTimerBased]);

  // ── Timer for crossfade & zoom ──
  const timerRef = useRef(null);
  useEffect(() => {
    if (!isTimerBased || images.length < 2) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length);
    }, speed);
    return () => clearInterval(timerRef.current);
  }, [isTimerBased, speed, images.length]);

  // Empty state
  if (images.length === 0) {
    return (
      <div style={{ ...style, rotate: `${element.rotation || 0}deg` }} className="select-none">
        <div
          className="w-full flex items-center justify-center border-2 border-dashed border-gray-300 bg-gray-100/50"
          style={{ aspectRatio }}
          data-testid={`slideshow-element-empty-${element.id}`}
        >
          <span className="text-xs text-gray-400">Add images in panel</span>
        </div>
      </div>
    );
  }

  // ── Zoom In transition ──
  if (transition === 'zoom') {
    return (
      <div style={{ ...style, rotate: `${element.rotation || 0}deg` }} className="select-none">
        <style>{`
          @keyframes slideshowZoomIn {
            from { transform: scale(1); }
            to { transform: scale(1.15); }
          }
        `}</style>
        <div
          className="relative w-full overflow-hidden"
          style={{ aspectRatio }}
          data-testid={`slideshow-element-${element.id}`}
        >
          {images.map((img, i) => {
            const isActive = i === current;
            return (
              <img
                key={`${i}-${current}`}
                src={img}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  opacity: isActive ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  animation: isActive ? `slideshowZoomIn ${speed}ms ease-out forwards` : 'none',
                  transform: isActive ? undefined : 'scale(1)',
                }}
                draggable={false}
              />
            );
          })}
          {images.length > 1 && (
            <Dots count={images.length} current={current} onDot={(i) => setCurrent(i)} />
          )}
        </div>
      </div>
    );
  }

  // ── Crossfade transition ──
  if (transition === 'crossfade') {
    return (
      <div style={{ ...style, rotate: `${element.rotation || 0}deg` }} className="select-none">
        <div
          className="relative w-full overflow-hidden"
          style={{ aspectRatio }}
          data-testid={`slideshow-element-${element.id}`}
        >
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
              style={{ opacity: i === current ? 1 : 0 }}
              draggable={false}
            />
          ))}
          {images.length > 1 && (
            <Dots count={images.length} current={current} onDot={(i) => setCurrent(i)} />
          )}
        </div>
      </div>
    );
  }

  // ── Slide transition (default) ──
  return (
    <div style={{ ...style, rotate: `${element.rotation || 0}deg` }} className="select-none">
      <div
        className="relative w-full overflow-hidden"
        data-testid={`slideshow-element-${element.id}`}
      >
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {images.map((img, i) => (
              <div key={i} className="min-w-0 flex-[0_0_100%]">
                <img
                  src={img}
                  alt=""
                  className="w-full object-cover"
                  style={{ aspectRatio }}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
        {images.length > 1 && (
          <Dots count={images.length} current={current} onDot={(i) => emblaApi?.scrollTo(i)} />
        )}
      </div>
    </div>
  );
}

function Dots({ count, current, onDot }) {
  return (
    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={(e) => { e.stopPropagation(); onDot(i); }}
          className="w-1.5 h-1.5 rounded-full transition-all"
          style={{ background: i === current ? '#fff' : 'rgba(255,255,255,0.4)' }}
        />
      ))}
    </div>
  );
}
